import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: 'All Categories', shortLabel: 'All', emoji: '🎯' },
  { id: 'food', label: 'Food & Dining', shortLabel: 'Food', emoji: '🍕' },
  { id: 'entertainment', label: 'Entertainment', shortLabel: 'Entertainment', emoji: '🎬' },
  { id: 'outdoor', label: 'Outdoor', shortLabel: 'Outdoor', emoji: '🌳' },
  { id: 'sports', label: 'Sports & Fitness', shortLabel: 'Sports', emoji: '⚽' },
  { id: 'cultural', label: 'Arts & Culture', shortLabel: 'Arts', emoji: '🎨' },
  { id: 'educational', label: 'Educational', shortLabel: 'Educational', emoji: '📚' },
  { id: 'social', label: 'Social', shortLabel: 'Social', emoji: '👥' },
  { id: 'creative', label: 'Creative', shortLabel: 'Creative', emoji: '🎨' },
  { id: 'adventure', label: 'Adventure', shortLabel: 'Adventure', emoji: '🏔️' },
  { id: 'relaxation', label: 'Relaxation', shortLabel: 'Relaxation', emoji: '🧘' },
  { id: 'shopping', label: 'Shopping', shortLabel: 'Shopping', emoji: '🛍️' },
  { id: 'other', label: 'Other', shortLabel: 'Other', emoji: '🎈' }
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All Activities', shortLabel: 'All' },
  { id: 'unrated', label: 'Not Yet Rated', shortLabel: 'Unrated' },
  { id: 'rated', label: 'Has Ratings', shortLabel: 'Rated' },
  { id: 'high-rated', label: 'Highly Rated (7+)', shortLabel: 'Top Rated' },
  { id: 'needs-votes', label: 'Needs More Votes', shortLabel: 'Need Votes' },
  { id: 'completed', label: 'Completed', shortLabel: 'Done' },
  { id: 'not-completed', label: 'Not Completed Yet', shortLabel: 'To Do' },
  { id: 'favorites', label: 'Favorites Only', shortLabel: 'Favorites' }
];

const RATING_FILTERS = [
  { id: 'all', label: 'Any Rating', shortLabel: 'Any' },
  { id: '8+', label: '8+ Family Favorite', shortLabel: '8+ Stars' },
  { id: '6-8', label: '6-8 Pretty Good', shortLabel: '6-8 Stars' },
  { id: '4-6', label: '4-6 Worth Trying', shortLabel: '4-6 Stars' },
  { id: 'unrated', label: 'Not Rated Yet', shortLabel: 'No Rating' }
];

export default function MobileActivityFilters({ 
  isOpen, 
  onClose, 
  onApplyFilters,
  initialFilters = {}
}) {
  const [activeCategories, setActiveCategories] = useState(
    initialFilters.categories || ['all']
  );
  const [statusFilter, setStatusFilter] = useState(
    initialFilters.status || 'all'
  );
  const [ratingFilter, setRatingFilter] = useState(
    initialFilters.rating || 'all'
  );
  const [showCompleted, setShowCompleted] = useState(
    initialFilters.showCompleted || false
  );
  const [showFavorites, setShowFavorites] = useState(
    initialFilters.showFavorites || false
  );
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);



  // Helper function to get appropriate label
  const getLabel = (item) => {
    return isMobile && item.shortLabel ? item.shortLabel : item.label;
  };

  // Handle category selection
  const handleCategoryToggle = (categoryId) => {
    let newCategories;
    if (categoryId === 'all') {
      newCategories = ['all'];
      setActiveCategories(newCategories);
    } else {
      setActiveCategories(prev => {
        const filtered = prev.filter(id => id !== 'all');
        
        if (filtered.includes(categoryId)) {
          newCategories = filtered.filter(id => id !== categoryId);
          newCategories = newCategories.length === 0 ? ['all'] : newCategories;
        } else {
          newCategories = [...filtered, categoryId];
        }
        
        // Apply filters with new categories
        const filters = {
          categories: newCategories,
          status: statusFilter,
          rating: ratingFilter
        };
        onApplyFilters(filters);
        
        return newCategories;
      });
    }
    
    // Apply filters for 'all' case
    if (categoryId === 'all') {
      const filters = {
        categories: ['all'],
        status: statusFilter,
        rating: ratingFilter
      };
      onApplyFilters(filters);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setActiveCategories(['all']);
    setStatusFilter('all');
    setRatingFilter('all');
    setShowCompleted(false);
    setShowFavorites(false);
    
    // Apply cleared filters
    const filters = {
      categories: ['all'],
      status: 'all',
      rating: 'all',
      showCompleted: false,
      showFavorites: false
    };
    onApplyFilters(filters);
  };

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="filter-panel" onClick={handleBackdropClick}>
      <div className="filter-content">
        {/* Header */}
        <div className="filter-header">
          <h3 className="filter-title">Filter Activities</h3>
          <button
            onClick={onClose}
            className="filter-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter Body */}
        <div className="filter-body">
          {/* Categories */}
          <div className="filter-section">
            <h4 className="filter-section-title">Categories</h4>
            <div className="filter-options">
              {CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryToggle(category.id)}
                  className={`filter-option ${
                    activeCategories.includes(category.id) ? 'active' : ''
                  }`}
                >
                  <span className="mr-2">{category.emoji}</span>
                  <span className="truncate">{getLabel(category)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="filter-section">
            <h4 className="filter-section-title">Activity Status</h4>
            <div className="filter-options">
              {STATUS_FILTERS.map((status) => (
                <button
                  key={status.id}
                  onClick={() => {
                    setStatusFilter(status.id);
                    const filters = {
                      categories: activeCategories,
                      status: status.id,
                      rating: ratingFilter,
                      showCompleted: status.id === 'completed' ? true : (status.id === 'not-completed' ? false : showCompleted),
                      showFavorites: status.id === 'favorites'
                    };
                    onApplyFilters(filters);
                  }}
                  className={`filter-option ${
                    statusFilter === status.id ? 'active' : ''
                  }`}
                >
                  <span className="truncate">{getLabel(status)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div className="filter-section">
            <h4 className="filter-section-title">Family Rating</h4>
            <div className="filter-options">
              {RATING_FILTERS.map((rating) => (
                <button
                  key={rating.id}
                  onClick={() => {
                    setRatingFilter(rating.id);
                    const filters = {
                      categories: activeCategories,
                      status: statusFilter,
                      rating: rating.id,
                      showCompleted: showCompleted
                    };
                    onApplyFilters(filters);
                  }}
                  className={`filter-option ${
                    ratingFilter === rating.id ? 'active' : ''
                  }`}
                >
                  <span className="truncate">{getLabel(rating)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Show Completed Toggle */}
        <div className="filter-section">
          <h4 className="filter-section-title">Special Filters</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <span className="text-sm font-medium text-stone-700">Show Completed Activities</span>
              <button
                onClick={() => {
                  const newValue = !showCompleted;
                  setShowCompleted(newValue);
                  const filters = {
                    categories: activeCategories,
                    status: statusFilter,
                    rating: ratingFilter,
                    showCompleted: newValue,
                    showFavorites: showFavorites
                  };
                  onApplyFilters(filters);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showCompleted ? 'bg-emerald-600' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showCompleted ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <span className="text-sm font-medium text-stone-700">Show Favorites Only</span>
              <button
                onClick={() => {
                  const newValue = !showFavorites;
                  setShowFavorites(newValue);
                  const filters = {
                    categories: activeCategories,
                    status: statusFilter,
                    rating: ratingFilter,
                    showCompleted: showCompleted,
                    showFavorites: newValue
                  };
                  onApplyFilters(filters);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showFavorites ? 'bg-yellow-500' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showFavorites ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Actions - Only Clear All button now */}
        <div className="filter-actions">
          <button
            onClick={handleClearFilters}
            className="filter-clear"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    </div>
  );
}